import {
  Activity,
  Clock,
  Gauge,
  GitCompareArrows,
  Leaf,
  LineChart,
  Map as MapIcon,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";

import { PointKindIcon } from "@/components/market-intelligence/PointKind";
import { SCOPE_LABELS } from "@/lib/market-intelligence/view-links";
import { cn } from "@/lib/utils";

import type { MyViewsGroup } from "@/lib/market-intelligence/my-views";
import type { ActiveMarketView } from "@/types/market-intelligence";
import type { MarketViewScope } from "@/types/market-views";
import type { LucideIcon } from "lucide-react";

/** Each chart's icon, as its tab and the region pages draw it. */
const SCOPE_ICONS: Record<MarketViewScope, LucideIcon> = {
  forwards_strips_rolling: TrendingUp,
  forwards_strips_calendar: TrendingUp,
  forwards_strips_quarterly: TrendingUp,
  forwards_curve: LineChart,
  index_trends: Activity,
  index_hourly: Clock,
  index_basis: GitCompareArrows,
  region_capacity: Gauge,
  region_recs: Leaf,
};

/**
 * The panel's views, one group per point (or region), each view a link to its
 * chart with the view applied.
 *
 * Two levels that read as two: the point is a small uppercase label, as the
 * module sidebar labels its sections, and its views sit a step in under it —
 * so a glance down the panel finds the point first, then the view. Each row names the chart as well as the view ("Winter hedge" means
 * nothing until it says Rolling strips). The view on screen is marked, and
 * says when it has changes not yet saved.
 */
export function MyViewsList({
  groups,
  active,
}: {
  groups: readonly MyViewsGroup[];
  active: ActiveMarketView | null;
}) {
  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => (
        <section key={group.key} className="flex flex-col gap-1.5">
          <h3 className="flex items-center gap-1.5 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group.kind ? (
              <PointKindIcon kind={group.kind} size={12} className="text-muted-foreground" />
            ) : (
              <MapIcon size={12} className="text-muted-foreground" aria-hidden />
            )}
            <span className="truncate">{group.title}</span>
            {group.region && (
              <span className="truncate font-medium text-text-dim">· {group.region}</span>
            )}
            {group.current && (
              <span className="ml-auto shrink-0 rounded-full bg-brand-accent-dim px-1.5 py-px text-xs font-medium normal-case tracking-normal text-brand-accent">
                This page
              </span>
            )}
          </h3>
          {/* A step in under the point — the indent alone carries the level. */}
          <ul className="flex flex-col gap-0.5 pl-4">
            {group.views.map(({ view, href }) => {
              const Icon = SCOPE_ICONS[view.scope];
              const isActive = active?.viewId === view.id;
              const body = (
                <>
                  <Icon
                    size={14}
                    strokeWidth={1.75}
                    className={cn(
                      "mt-0.5 shrink-0",
                      isActive ? "text-brand-accent" : "text-muted-foreground",
                    )}
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-foreground">{view.name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {href ? SCOPE_LABELS[view.scope] : `${SCOPE_LABELS[view.scope]} · no longer published`}
                    </span>
                  </span>
                  {isActive && (
                    <span className="mt-0.5 shrink-0 text-xs font-medium text-brand-accent">
                      {active?.dirty ? "Unsaved" : "Open"}
                    </span>
                  )}
                </>
              );
              const rowClass = cn(
                "flex items-start gap-2.5 rounded-md px-2 py-1.5 text-left",
                isActive && "bg-brand-accent-dim",
              );
              return (
                <li key={view.id}>
                  {href ? (
                    <Link
                      to={href}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        rowClass,
                        "transition-colors hover:bg-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-mid",
                      )}
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className={cn(rowClass, "opacity-60")}>{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
